import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useRecording } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { 
  Flame, Swords, Play, Pause, Square, CheckCircle, 
  ThumbsUp, Video, X, TrendingUp,
  Clock, Users, Loader2, RotateCw
} from "lucide-react";

// Matchup Color Palettes - Rotating, non-political
const MATCHUP_PALETTES = [
  { name: 'charcoal-ember', a: '#2B2F36', b: '#FF7A18', aText: '#FFFFFF', bText: '#FFFFFF' },
  { name: 'indigo-gold', a: '#3B2EED', b: '#F5B301', aText: '#FFFFFF', bText: '#000000' },
  { name: 'teal-magenta', a: '#1FB6A6', b: '#E23D8F', aText: '#FFFFFF', bText: '#FFFFFF' },
  { name: 'slate-electric', a: '#3A4654', b: '#FFD400', aText: '#FFFFFF', bText: '#000000' },
];

// Get palette based on matchup ID (deterministic rotation)
const getMatchupPalette = (matchupId) => {
  if (!matchupId) return MATCHUP_PALETTES[0];
  const hash = matchupId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return MATCHUP_PALETTES[hash % MATCHUP_PALETTES.length];
};

// Heat Meter Component - Uses matchup palette colors
const HeatMeter = ({ heatA, heatB, sideA, sideB, votesA, votesB, palette }) => {
  const colors = palette || MATCHUP_PALETTES[0];
  
  return (
    <div className="w-full">
      <div className="flex justify-between text-sm mb-2">
        <span className="font-bold" style={{ color: colors.a }}>SIDE A</span>
        <span className="font-bold" style={{ color: colors.b }}>SIDE B</span>
      </div>
      <div className="relative h-4 rounded-full overflow-hidden bg-zinc-800">
        <div 
          className="absolute left-0 top-0 h-full transition-all duration-500"
          style={{ width: `${heatA}%`, backgroundColor: colors.a }}
        />
        <div 
          className="absolute right-0 top-0 h-full transition-all duration-500"
          style={{ width: `${heatB}%`, backgroundColor: colors.b }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-bold text-white drop-shadow-lg">
            {votesA + votesB > 0 ? `${heatA}% - ${heatB}%` : "Vote to see results!"}
          </span>
        </div>
      </div>
      <div className="flex justify-between text-xs text-muted-foreground mt-1">
        <span>{votesA} votes</span>
        <span>{votesB} votes</span>
      </div>
    </div>
  );
};

// Arena Take Card - shows a submitted video take
const TakeCard = ({ take }) => {
  const [playing, setPlaying] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [showRotationBadge, setShowRotationBadge] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef(null);
  const badgeTimeoutRef = useRef(null);
  
  // Show badge temporarily then fade out
  const showBadgeTemporarily = () => {
    setShowRotationBadge(true);
    if (badgeTimeoutRef.current) clearTimeout(badgeTimeoutRef.current);
    badgeTimeoutRef.current = setTimeout(() => {
      setShowRotationBadge(false);
    }, 2000);
  };
  
  // Manual rotate button - cycles through 0, 90, 180, 270
  const handleManualRotate = (e) => {
    e.stopPropagation();
    setRotation((prev) => (prev + 90) % 360);
    showBadgeTemporarily();
  };
  
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };
  
  const handleStop = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setPlaying(false);
    }
  };
  
  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (badgeTimeoutRef.current) clearTimeout(badgeTimeoutRef.current);
    };
  }, []);
  
  // Calculate video styles based on rotation
  const getVideoStyle = () => {
    const baseStyle = {
      transform: 'translateZ(0)',
      willChange: 'transform',
      backfaceVisibility: 'hidden',
      WebkitBackfaceVisibility: 'hidden'
    };
    
    if (rotation === 0) {
      return baseStyle;
    }
    
    // For 90° or 270° rotations, we need to swap dimensions
    const needsSwap = rotation === 90 || rotation === 270;
    
    return {
      ...baseStyle,
      transform: `translateZ(0) rotate(${rotation}deg)`,
      ...(needsSwap && {
        width: '177.78%',
        height: '56.25%',
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginLeft: '-88.89%',
        marginTop: '-28.125%'
      })
    };
  };
  
  return (
    <Card className="bg-card/50 border-white/10 overflow-hidden">
      <div 
        className="relative aspect-video bg-black overflow-hidden"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={() => setIsHovered(true)}
      >
        <video
          ref={videoRef}
          src={take.video_url}
          className={`w-full h-full object-contain ${rotation === 0 ? '' : 'absolute inset-0'}`}
          style={getVideoStyle()}
          playsInline
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
        />
        
        {/* Play/Pause/Stop overlay */}
        <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${playing ? 'opacity-0 hover:opacity-100' : 'opacity-100'} bg-black/50 hover:bg-black/40 z-20`}>
          <div className="flex items-center gap-4">
            {playing && (
              <button
                onClick={handleStop}
                className="p-3 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                title="Stop"
              >
                <Square className="w-6 h-6 text-white fill-white" />
              </button>
            )}
            <button
              onClick={handlePlayPause}
              className="p-4 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              title={playing ? "Pause" : "Play"}
            >
              {playing ? (
                <Pause className="w-10 h-10 text-white fill-white" />
              ) : (
                <Play className="w-10 h-10 text-white" />
              )}
            </button>
          </div>
        </div>
        
        {/* Side badge */}
        <Badge 
          className={`absolute top-2 left-2 ${take.side === 'a' ? 'bg-red-600' : 'bg-blue-600'} z-30`}
        >
          Side {take.side.toUpperCase()}
        </Badge>
        
        {/* Manual Rotate Button - only visible on hover/tap */}
        <button
          onClick={handleManualRotate}
          className={`absolute top-2 right-2 p-2 rounded-full bg-black/70 hover:bg-black/90 transition-all z-30 ${
            isHovered || !playing ? 'opacity-100' : 'opacity-0'
          }`}
          title="Rotate video"
        >
          <RotateCw className="w-4 h-4 text-white" />
        </button>
        
        {/* Rotation indicator - shows briefly then fades */}
        {rotation !== 0 && (
          <div 
            className={`absolute bottom-2 right-2 bg-black/60 text-white/80 text-xs px-2 py-1 rounded z-30 transition-opacity duration-500 ${
              showRotationBadge ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {rotation}°
          </div>
        )}
      </div>
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Avatar className="w-8 h-8">
              <AvatarImage src={take.picture} />
              <AvatarFallback>{take.username?.[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <span className="text-sm font-medium">{take.username}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <ThumbsUp className="w-4 h-4" />
            <span className="text-sm">{take.votes}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Arena Recording Screen - Self-contained but uses same recording patterns as Focus Mode
const ArenaRecordingScreen = ({ matchup, side, token, onComplete, onCancel }) => {
  const { setIsRecording: setGlobalRecording } = useRecording();
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const uploadAbortControllerRef = useRef(null); // For canceling uploads
  
  const [status, setStatus] = useState("preview"); // preview, recording, uploading, done
  const [timeLeft, setTimeLeft] = useState(60);
  const [previewReady, setPreviewReady] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  
  const maxTime = 60;
  
  // Initialize camera - same pattern as Focus Mode with improved framerate
  useEffect(() => {
    let mounted = true;
    
    // Lock orientation on mobile when component mounts
    const lockOrientation = async () => {
      try {
        if (screen.orientation?.lock) {
          // Lock to current orientation to prevent rotation issues
          await screen.orientation.lock('portrait').catch(() => {});
        }
      } catch (e) {
        console.log("[Arena] Orientation lock not supported");
      }
    };
    lockOrientation();
    
    const initCamera = async () => {
      try {
        // Detect Mac for optimized constraints
        const isMac = /Macintosh|MacIntel|MacPPC|Mac68K/.test(navigator.platform);
        const isDesktop = !(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
        
        const videoConstraints = {
          facingMode: "user",
          aspectRatio: { ideal: 16/9 }
        };
        
        if (isMac && isDesktop) {
          // Mac desktop: use 720p max for smoother preview/recording
          console.log('[Arena] Mac desktop - using optimized constraints');
          videoConstraints.width = { ideal: 1280, max: 1280 };
          videoConstraints.height = { ideal: 720, max: 720 };
          videoConstraints.frameRate = { ideal: 30, max: 30 };
        } else {
          videoConstraints.width = { ideal: 1280, min: 640 };
          videoConstraints.height = { ideal: 720, min: 360 };
          videoConstraints.frameRate = { ideal: 30, min: 24, max: 60 };
        }
        
        const stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: true
        });
        
        if (!mounted) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        
        // Mute audio during preview
        stream.getAudioTracks().forEach(t => t.enabled = false);
        
        streamRef.current = stream;
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.muted = true;
          videoRef.current.playsInline = true;
          videoRef.current.setAttribute('playsinline', '');
          
          await videoRef.current.play();
          
          // Mac optimization: Use requestVideoFrameCallback for smoother rendering
          if (isMac && 'requestVideoFrameCallback' in HTMLVideoElement.prototype) {
            console.log('[Arena] Mac: Using requestVideoFrameCallback for smoother rendering');
            const scheduleFrame = () => {
              if (videoRef.current && !videoRef.current.paused && !videoRef.current.ended) {
                videoRef.current.requestVideoFrameCallback(scheduleFrame);
              }
            };
            scheduleFrame();
          }
          
          setPreviewReady(true);
        }
      } catch (err) {
        console.error("[Arena] Camera error:", err);
        setError("Could not access camera. Please allow camera permissions.");
      }
    };
    
    initCamera();
    
    return () => {
      mounted = false;
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current && status !== "uploading") {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      // Unlock orientation on unmount
      try {
        if (screen.orientation?.unlock) {
          screen.orientation.unlock();
        }
      } catch (e) {
        // Orientation unlock not supported - ignore
      }
    };
  }, []);
  
  // Store the orientation when recording starts - used for playback correction
  const startOrientationRef = useRef(null);
  
  // Handle recording start
  const handleStartRecording = async () => {
    if (!streamRef.current) return;
    
    setError(null);
    
    // Capture and lock the start orientation
    const isPortrait = window.innerHeight > window.innerWidth;
    startOrientationRef.current = isPortrait ? 'portrait' : 'landscape';
    console.log('[Arena] Recording started in orientation:', startOrientationRef.current);
    
    // Enable audio
    streamRef.current.getAudioTracks().forEach(t => t.enabled = true);
    
    // Ensure video stays visible
    if (videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      await videoRef.current.play().catch(() => {});
    }
    
    chunksRef.current = [];
    
    // Find supported format
    const formats = ['video/mp4', 'video/webm;codecs=vp9', 'video/webm'];
    let mimeType = '';
    for (const format of formats) {
      if (MediaRecorder.isTypeSupported(format)) {
        mimeType = format;
        break;
      }
    }
    
    const options = {
      videoBitsPerSecond: 4000000,
      audioBitsPerSecond: 128000
    };
    if (mimeType) options.mimeType = mimeType;
    
    const recorder = new MediaRecorder(streamRef.current, options);
    
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    
    recorder.onstart = () => {
      console.log("[Arena] Recording started");
      setStatus("recording");
      setGlobalRecording(true);
      setTimeLeft(maxTime);
      
      // Start timer
      const startTime = performance.now();
      timerRef.current = setInterval(() => {
        const elapsed = (performance.now() - startTime) / 1000;
        const remaining = Math.max(0, Math.ceil(maxTime - elapsed));
        setTimeLeft(remaining);
        
        if (remaining <= 0) {
          handleStopRecording();
        }
      }, 200);
    };
    
    recorder.onerror = (e) => {
      console.error("[Arena] Recorder error:", e);
      setError("Recording failed. Please try again.");
      setStatus("preview");
    };
    
    mediaRecorderRef.current = recorder;
    recorder.start(500);
  };
  
  // Handle recording stop
  const handleStopRecording = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    if (mediaRecorderRef.current?.state === "recording") {
      setStatus("uploading");
      setGlobalRecording(false);
      
      mediaRecorderRef.current.onstop = async () => {
        await handleUpload();
      };
      
      mediaRecorderRef.current.stop();
    }
  };
  
  // Handle upload to Arena endpoint with retry logic
  const handleUpload = async () => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000; // 2 seconds between retries
    
    const mimeType = mediaRecorderRef.current?.mimeType || 'video/webm';
    const blob = new Blob(chunksRef.current, { type: mimeType });
    
    console.log("[Arena] Uploading blob:", blob.size, "bytes, start orientation:", startOrientationRef.current);
    
    const formData = new FormData();
    formData.append("video", blob, `arena_take.${mimeType.includes('mp4') ? 'mp4' : 'webm'}`);
    formData.append("matchup_id", matchup.matchup_id);
    formData.append("side", side);
    formData.append("start_orientation", startOrientationRef.current || 'portrait');
    
    // Create abort controller for this upload
    uploadAbortControllerRef.current = new AbortController();
    
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        setUploadProgress(0);
        
        if (attempt > 1) {
          toast.info(`Retrying upload... (attempt ${attempt}/${MAX_RETRIES})`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        }
        
        await axios.post(`${API}/arena/take`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          },
          timeout: 60000, // 60 second timeout
          signal: uploadAbortControllerRef.current.signal,
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        });
        
        // Stop camera
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
        }
        
        toast.success("Take submitted! Awaiting approval.");
        uploadAbortControllerRef.current = null;
        setStatus("done");
        setTimeout(() => onComplete(), 1000);
        return; // Success, exit the retry loop
        
      } catch (err) {
        // Check if cancelled by user
        if (axios.isCancel(err) || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          console.log("[Arena] Upload cancelled by user");
          setStatus("preview");
          setUploadProgress(0);
          toast.info("Upload cancelled");
          uploadAbortControllerRef.current = null;
          return;
        }
        
        console.error(`[Arena] Upload error (attempt ${attempt}):`, err);
        
        if (attempt === MAX_RETRIES) {
          // Final attempt failed
          toast.error(err.response?.data?.detail || "Failed to upload take after multiple attempts");
          setError("Upload failed. Please try again.");
          setStatus("preview");
          uploadAbortControllerRef.current = null;
        }
        // If not final attempt, the loop will continue and retry
      }
    }
  };
  
  // Cancel upload handler
  const handleCancelUpload = () => {
    if (uploadAbortControllerRef.current) {
      console.log('[Arena] Cancelling upload...');
      uploadAbortControllerRef.current.abort();
    }
  };
  
  // Handle cancel/exit
  const handleExit = () => {
    if (status === "uploading") {
      if (!window.confirm("Upload in progress! Leaving now may lose your recording. Are you sure?")) {
        return;
      }
      // Cancel the upload if user confirms exit
      handleCancelUpload();
    }
    
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    setGlobalRecording(false);
    onCancel();
  };

  // Premium gradient based on side
  const sideGradient = side === 'a' 
    ? 'from-red-600/20 via-transparent to-transparent'
    : 'from-blue-600/20 via-transparent to-transparent';
  
  const sideBorderColor = side === 'a' ? 'border-red-500/40' : 'border-blue-500/40';
  const sideGlowColor = side === 'a' ? 'shadow-red-500/20' : 'shadow-blue-500/20';

  return (
    <div 
      className="fixed inset-0 z-50 bg-gradient-to-b from-zinc-900 to-black flex flex-col"
      style={{
        height: '100dvh',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)'
      }}
    >
      {/* Premium Header with subtle gradient */}
      <div className={`shrink-0 relative flex items-center justify-between px-4 py-3 bg-gradient-to-r ${sideGradient}`}>
        <div className="flex items-center gap-2">
          <Badge className={`${side === 'a' ? 'bg-red-600' : 'bg-blue-600'} shadow-lg`}>
            Side {side.toUpperCase()}
          </Badge>
        </div>
        
        {/* Timer - centered, visible during recording */}
        <div className="absolute left-1/2 -translate-x-1/2">
          {status === "recording" && (
            <div className={`px-5 py-2 rounded-full font-mono text-xl font-bold backdrop-blur-sm ${
              timeLeft <= 10 ? 'bg-red-600 animate-pulse shadow-lg shadow-red-500/50' : 'bg-black/40 border border-white/20'
            }`}>
              {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}
            </div>
          )}
        </div>
        
        {status !== "uploading" && (
          <Button variant="ghost" size="sm" onClick={handleExit} className="hover:bg-white/10">
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
      
      {/* Main Recording Area - Premium frame design */}
      <div className="flex-1 relative overflow-hidden p-3">
        {/* Premium video frame with gradient border */}
        <div className={`relative w-full h-full rounded-2xl overflow-hidden border-2 ${sideBorderColor} shadow-2xl ${sideGlowColor} bg-black`}>
          {/* Video Preview - with GPU acceleration for smoother preview */}
          <video
            ref={videoRef}
            className="w-full h-full object-contain"
            style={{ 
              transform: 'scaleX(-1)',
              willChange: 'transform',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden'
            }}
            playsInline
            muted
            autoPlay
          />
          
          {/* Premium corner accents */}
          <div className={`absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 ${sideBorderColor} rounded-tl-xl`}></div>
          <div className={`absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 ${sideBorderColor} rounded-tr-xl`}></div>
          <div className={`absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 ${sideBorderColor} rounded-bl-xl`}></div>
          <div className={`absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 ${sideBorderColor} rounded-br-xl`}></div>
          
          {/* Preview loading state */}
          {!previewReady && status === "preview" && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/90">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-3" />
                <p className="text-muted-foreground">Starting camera...</p>
              </div>
            </div>
          )}
          
          {/* Recording indicator - premium style */}
          {status === "recording" && (
            <div className="absolute top-4 left-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-red-600/90 backdrop-blur-sm rounded-full shadow-lg">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                <span className="text-sm font-bold">REC</span>
              </div>
            </div>
          )}
          
          {/* Uploading overlay - clean design without stuck timer */}
          {status === "uploading" && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-50 rounded-2xl">
              <div className="text-center p-6 max-w-sm">
                {/* Spinner */}
                <div className="relative w-16 h-16 mx-auto mb-6">
                  <div className="absolute inset-0 rounded-full border-4 border-white/10"></div>
                  <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-green-500 animate-spin"></div>
                </div>
                
                {/* Text */}
                <h3 className="text-xl font-bold italic text-white mb-2">Uploading your response...</h3>
                <p className="text-muted-foreground mb-4">Please wait, don&apos;t leave this screen</p>
                
                {/* Progress bar */}
                {uploadProgress > 0 && (
                  <div className="w-full bg-white/10 rounded-full h-2 mb-4">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
                
                {/* Warning box */}
                <div className="p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg">
                  <p className="text-sm text-yellow-400">⏳ This may take a moment for longer recordings</p>
                </div>
                
                {/* Cancel upload button */}
                <button
                  onClick={handleCancelUpload}
                  className="mt-4 px-4 py-2 text-sm text-white/70 hover:text-white border border-white/20 hover:border-white/40 rounded-lg transition-colors"
                >
                  Cancel Upload
                </button>
              </div>
            </div>
          )}
          
          {/* Error state */}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-2xl">
              <div className="text-center p-6">
                <X className="w-12 h-12 text-red-500 mx-auto mb-3" />
                <p className="text-red-400 mb-4">{error}</p>
                <Button onClick={() => setError(null)}>Try Again</Button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Footer Controls - Premium design */}
      <div className="shrink-0 px-4 py-4 bg-gradient-to-t from-black/80 to-transparent">
        <div className="flex items-center justify-between gap-3">
          {/* Left: Record button or status */}
          <div className="flex-1">
            {status === "preview" && previewReady && (
              <Button 
                onClick={handleStartRecording}
                className={`${side === 'a' ? 'bg-red-600 hover:bg-red-700 shadow-lg shadow-red-500/30' : 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/30'} font-bold`}
              >
                <Video className="w-4 h-4 mr-2" />
                Record
              </Button>
            )}
            {status === "recording" && (
              <div className="flex items-center gap-2 px-4 py-2 bg-red-600/20 border border-red-500/50 rounded-full">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                <span className="text-sm font-bold text-red-400">Recording</span>
              </div>
            )}
            {status === "uploading" && (
              <div className="flex items-center gap-2 px-4 py-2 bg-green-600/20 border border-green-500/50 rounded-full">
                <Loader2 className="w-4 h-4 animate-spin text-green-400" />
                <span className="text-sm font-bold text-green-400">Uploading</span>
              </div>
            )}
          </div>
          
          {/* Center: Done button */}
          <div className="flex-1 flex justify-center">
            {status === "recording" && (
              <Button 
                onClick={handleStopRecording}
                className="bg-green-600 hover:bg-green-700 text-white font-bold px-10 py-6 text-lg shadow-lg shadow-green-500/30 rounded-xl"
              >
                <CheckCircle className="w-6 h-6 mr-2" />
                Done
              </Button>
            )}
          </div>
          
          {/* Right: Exit */}
          <div className="flex-1 flex justify-end">
            <Button
              onClick={handleExit}
              variant="outline"
              className={`border-white/20 text-white rounded-xl ${status === "uploading" ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/10'}`}
              disabled={status === "uploading"}
            >
              <X className="w-4 h-4 mr-2" />
              Exit
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Arena Page
const ArenaPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [matchup, setMatchup] = useState(null);
  const [stats, setStats] = useState(null);
  const [takes, setTakes] = useState([]);
  const [myVote, setMyVote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSide, setSelectedSide] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [viewingSide, setViewingSide] = useState(null);
  
  // Fetch matchup data
  const fetchMatchup = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/arena/current`);
      setMatchup(res.data.matchup);
      setStats(res.data.stats);
      
      if (res.data.matchup) {
        const takesRes = await axios.get(`${API}/arena/takes/${res.data.matchup.matchup_id}`);
        setTakes(takesRes.data.takes);
        
        if (token) {
          try {
            const voteRes = await axios.get(`${API}/arena/vote/${res.data.matchup.matchup_id}/me`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setMyVote(voteRes.data.vote?.side);
          } catch (e) {
            // No vote yet
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch arena:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  useEffect(() => {
    fetchMatchup();
  }, [fetchMatchup]);
  
  const handleVote = async (side) => {
    if (!user) {
      toast.error("Please log in to vote");
      navigate("/login");
      return;
    }
    
    try {
      await axios.post(`${API}/arena/vote/${matchup.matchup_id}`, null, {
        params: { side },
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyVote(side);
      toast.success(`Voted for ${side === 'a' ? matchup.side_a : matchup.side_b}!`);
      fetchMatchup();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to vote");
    }
  };
  
  const handleChooseSide = (side) => {
    if (!user) {
      toast.error("Please log in to record a take");
      navigate("/login");
      return;
    }
    setSelectedSide(side);
    setIsRecording(true);
  };
  
  const handleRecordComplete = () => {
    setIsRecording(false);
    setSelectedSide(null);
    fetchMatchup();
  };
  
  // Show recorder if recording
  if (isRecording && selectedSide && matchup) {
    return (
      <ArenaRecordingScreen
        matchup={matchup}
        side={selectedSide}
        token={token}
        onComplete={handleRecordComplete}
        onCancel={() => {
          setIsRecording(false);
          setSelectedSide(null);
        }}
      />
    );
  }
  
  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Ambient glow - Ember accent based (non-political) */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[20%] -left-[20%] w-[70%] h-[50%] rounded-full bg-[#FF7A18]/15 blur-[100px]" />
        <div className="absolute -bottom-[20%] -right-[20%] w-[70%] h-[50%] rounded-full bg-[#FF9A4A]/10 blur-[100px]" />
      </div>
      
      <Navbar />
      
      <div className="relative max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Flame className="w-8 h-8 text-orange-500" />
            <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tight">
              THE ARENA
            </h1>
            <Flame className="w-8 h-8 text-orange-500" />
          </div>
          <p className="text-lg text-muted-foreground font-medium">Daily Spar</p>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : !matchup ? (
          <Card className="bg-card/50 border-white/10 text-center py-12">
            <CardContent>
              <Swords className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">No Active Matchup</h2>
              <p className="text-muted-foreground">Check back later for today&apos;s matchup!</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Get the color palette for this matchup */}
            {(() => {
              const palette = getMatchupPalette(matchup?.matchup_id);
              return (
            <>
            {/* Today's Matchup Card */}
            <Card className="bg-gradient-to-br from-zinc-900 to-zinc-950 border-2 border-[#FF7A18]/40 mb-6 overflow-hidden shadow-xl shadow-[#FF7A18]/10">
              <CardHeader className="border-b border-white/10 pb-4">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-[#FF7A18] border-[#FF7A18]/50 font-bold">
                    <Clock className="w-3 h-3 mr-1" />
                    Today&apos;s Matchup
                  </Badge>
                  {stats && (
                    <Badge variant="secondary" className="text-xs font-bold">
                      <Users className="w-3 h-3 mr-1" />
                      {stats.total_votes} votes
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {/* VS Display - Using palette colors with SIDE labels */}
                <div className="bg-black/50 rounded-xl p-6 mb-6 border border-white/10">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 text-center">
                      <div 
                        className="w-20 h-20 rounded-full flex flex-col items-center justify-center mx-auto mb-3 border-2"
                        style={{ 
                          backgroundColor: `${palette.a}30`,
                          borderColor: `${palette.a}80`
                        }}
                      >
                        <span className="text-[10px] font-bold uppercase tracking-wider opacity-70" style={{ color: palette.a }}>SIDE</span>
                        <span className="text-3xl font-black" style={{ color: palette.a }}>A</span>
                      </div>
                      <h3 className="font-black text-xl md:text-2xl text-white leading-tight drop-shadow-lg">{matchup.side_a}</h3>
                    </div>
                    
                    <div className="shrink-0 flex flex-col items-center">
                      <span className="text-3xl md:text-4xl font-black text-[#FF7A18] drop-shadow-lg">VS</span>
                    </div>
                    
                    <div className="flex-1 text-center">
                      <div 
                        className="w-20 h-20 rounded-full flex flex-col items-center justify-center mx-auto mb-3 border-2"
                        style={{ 
                          backgroundColor: `${palette.b}30`,
                          borderColor: `${palette.b}80`
                        }}
                      >
                        <span className="text-[10px] font-bold uppercase tracking-wider opacity-70" style={{ color: palette.b }}>SIDE</span>
                        <span className="text-3xl font-black" style={{ color: palette.b }}>B</span>
                      </div>
                      <h3 className="font-black text-xl md:text-2xl text-white leading-tight drop-shadow-lg">{matchup.side_b}</h3>
                    </div>
                  </div>
                </div>
                
                {matchup.description && (
                  <p className="text-sm text-muted-foreground text-center mb-6">{matchup.description}</p>
                )}
                
                {/* Heat Meter */}
                {stats && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="w-4 h-4 text-[#FF7A18]" />
                      <span className="text-sm font-medium">Heat Meter</span>
                      {stats.trending_side !== "tied" && (
                        <Badge 
                          variant="outline" 
                          className="text-xs"
                          style={{ 
                            color: stats.trending_side === 'a' ? palette.a : palette.b,
                            borderColor: `${stats.trending_side === 'a' ? palette.a : palette.b}50`
                          }}
                        >
                          🔥 Side {stats.trending_side.toUpperCase()} is trending!
                        </Badge>
                      )}
                    </div>
                    <HeatMeter 
                      heatA={stats.heat_a}
                      heatB={stats.heat_b}
                      sideA={matchup.side_a}
                      sideB={matchup.side_b}
                      votesA={stats.votes_a}
                      votesB={stats.votes_b}
                      palette={palette}
                    />
                  </div>
                )}
                
                {/* Action Buttons - Using palette colors */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <Button
                    onClick={() => handleVote('a')}
                    variant={myVote === 'a' ? 'default' : 'outline'}
                    className="h-14 min-w-0 flex flex-col gap-0.5 px-2"
                    style={myVote === 'a' 
                      ? { backgroundColor: palette.a, color: palette.aText }
                      : { borderColor: `${palette.a}80`, color: palette.a }
                    }
                  >
                    <span className="flex items-center gap-1.5">
                      <ThumbsUp className="w-4 h-4 flex-shrink-0" />
                      <span className="font-bold">SIDE A</span>
                    </span>
                    <span className="text-xs opacity-75 truncate w-full max-w-[140px]">{matchup.side_a}</span>
                  </Button>
                  <Button
                    onClick={() => handleVote('b')}
                    variant={myVote === 'b' ? 'default' : 'outline'}
                    className="h-14 min-w-0 flex flex-col gap-0.5 px-2"
                    style={myVote === 'b' 
                      ? { backgroundColor: palette.b, color: palette.bText }
                      : { borderColor: `${palette.b}80`, color: palette.b }
                    }
                  >
                    <span className="flex items-center gap-1.5">
                      <ThumbsUp className="w-4 h-4 flex-shrink-0" />
                      <span className="font-bold">SIDE B</span>
                    </span>
                    <span className="text-xs opacity-75 truncate w-full max-w-[140px]">{matchup.side_b}</span>
                  </Button>
                </div>
                
                {/* Record Take Buttons - Using palette colors */}
                {user && (
                  <div className="border-t border-white/10 pt-4 mt-4">
                    <p className="text-sm text-muted-foreground text-center mb-3">
                      Record your 60-second take
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        onClick={() => handleChooseSide('a')}
                        className="border"
                        style={{ 
                          backgroundColor: `${palette.a}20`,
                          borderColor: `${palette.a}80`,
                          color: palette.a
                        }}
                      >
                        <Video className="w-4 h-4 mr-2" />
                        Side A
                      </Button>
                      <Button
                        onClick={() => handleChooseSide('b')}
                        className="border"
                        style={{ 
                          backgroundColor: `${palette.b}20`,
                          borderColor: `${palette.b}80`,
                          color: palette.b
                        }}
                      >
                        <Video className="w-4 h-4 mr-2" />
                        Side B
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            </>
              );
            })()}
            
            {/* Takes Section */}
            {takes.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Play className="w-5 h-5" />
                    Arena Takes
                  </h2>
                  <div className="flex gap-2">
                    <Button
                      variant={viewingSide === null ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewingSide(null)}
                    >
                      All
                    </Button>
                    <Button
                      variant={viewingSide === 'a' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewingSide('a')}
                      className={viewingSide === 'a' ? 'bg-[#2B2F36]' : ''}
                    >
                      Side A
                    </Button>
                    <Button
                      variant={viewingSide === 'b' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewingSide('b')}
                      className={viewingSide === 'b' ? 'bg-[#FF7A18]' : ''}
                    >
                      Side B
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {takes
                    .filter(t => !viewingSide || t.side === viewingSide)
                    .map(take => (
                      <TakeCard key={take.take_id} take={take} />
                    ))
                  }
                </div>
              </div>
            )}
            
            {takes.length === 0 && (
              <Card className="bg-card/30 border-white/10 text-center py-8">
                <CardContent>
                  <Video className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No takes yet. Be the first!</p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ArenaPage;
